import React, { useState, useEffect, useRef, useMemo } from "react";
import styles from "./Modal.module.css";
import { X } from "lucide-react";
import { useModalContext } from "../../contexts/ModalContext";

// Generate UUID using crypto.randomUUID (available in modern browsers)
const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const Modal = ({
  header,
  children,
  isOpen,
  closeModal,
  resizable = true,
  style,
  minWidth,
  onRegister
}: {
  header: string | React.ReactNode;
  children?: React.ReactNode;
  isOpen: boolean;
  closeModal?: () => void;
  resizable?: boolean;
  style?: React.CSSProperties;
  minWidth?: string | number;
   onRegister?: (bringToFront: () => void) => void; 
}) => {
  const { registerModal, unregisterModal, bringToTop, getZIndex } =
    useModalContext();
  const modalId = useMemo(() => generateUUID(), []); // Generate UUID once and keep it constant
  useEffect(() => {
    if (isOpen) {
    registerModal(modalId);
    onRegister?.(() => bringToTop(modalId));  // ADD THIS LINE
  } else {
    unregisterModal(modalId);
  }
  return () => unregisterModal(modalId);
  }, [isOpen, modalId]);
  // Storage key for this modal
  const storageKey = `modal-${modalId}`;

  // Helper functions to get/set stored position and size
  const getStoredPosition = (): { x: number; y: number } | null => {
    try {
      const stored = localStorage.getItem(`${storageKey}-position`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore errors
    }
    return null;
  };

  const getStoredSize = (): { width: number; height: number } | null => {
    try {
      const stored = localStorage.getItem(`${storageKey}-size`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore errors
    }
    return null;
  };

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState(
    () => getStoredPosition() || { x: 0, y: 0 }
  );
  const [size, setSize] = useState(() => {
    const stored = getStoredSize();
    return stored || { width: 0, height: 0 };
  });
  const [hasBeenPositioned, setHasBeenPositioned] = useState(
    () => !!getStoredPosition()
  );
  const modalRef = useRef<HTMLDivElement>(null);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 });
  const sizeRef = useRef({ width: 0, height: 0 });
  const resizeDirectionRef = useRef<string>("");
  const zIndex = getZIndex(modalId);

  // Save position to localStorage whenever it changes (after initial positioning)
  useEffect(() => {
    if (hasBeenPositioned) {
      try {
        localStorage.setItem(
          `${storageKey}-position`,
          JSON.stringify(position)
        );
      } catch {
        // Ignore errors
      }
    }
  }, [position, hasBeenPositioned, storageKey]);

  // Save size to localStorage whenever it changes (for resizable modals)
  useEffect(() => {
    if (resizable && size.width > 0 && size.height > 0) {
      try {
        localStorage.setItem(`${storageKey}-size`, JSON.stringify(size));
      } catch {
        // Ignore errors
      }
    }
  }, [size, resizable, storageKey]);

  // Register/unregister modal when it opens/closes
  useEffect(() => {
    if (isOpen) {
      registerModal(modalId);
    } else {
      unregisterModal(modalId);
    }
    return () => {
      unregisterModal(modalId);
    };
  }, [isOpen, modalId, registerModal, unregisterModal]);

  // Keep positionRef and sizeRef in sync with state
  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  // Position modal on open - use stored position if available, otherwise center
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Use requestAnimationFrame to ensure modal is fully rendered before measuring
      const positionModal = () => {
        if (modalRef.current) {
          const modalRect = modalRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          // If we have a stored position, use it (and clamp to viewport if needed)
          if (hasBeenPositioned) {
            const storedPos = getStoredPosition();
            if (storedPos) {
              // Clamp position to ensure modal stays within viewport bounds
              const clampedX = Math.max(
                0,
                Math.min(storedPos.x, viewportWidth - 100)
              );
              const clampedY = Math.max(
                0,
                Math.min(storedPos.y, viewportHeight - 100)
              );
              setPosition({ x: clampedX, y: clampedY });
            }
          } else {
            // First time opening - center it
            const centeredX = (viewportWidth - modalRect.width) / 2;
            const centeredY = (viewportHeight - modalRect.height) / 2;
            setPosition({ x: centeredX, y: centeredY });
            setHasBeenPositioned(true);
          }

          // Set initial size if resizable
          if (resizable) {
            const storedSize = getStoredSize();
            if (storedSize && storedSize.width > 0 && storedSize.height > 0) {
              setSize(storedSize);
            } else if (size.width === 0 && size.height === 0) {
              setSize({ width: modalRect.width, height: modalRect.height });
            }
          }
        }
      };

      requestAnimationFrame(positionModal);
    }
  }, [isOpen, resizable, hasBeenPositioned, storageKey]);

  const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    bringToTop(modalId);
  };

  const handleResizeStart = (direction: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    resizeDirectionRef.current = direction;
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    bringToTop(modalId);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizing && modalRef.current && resizable) {
        const deltaX = e.clientX - lastMousePosRef.current.x;
        const deltaY = e.clientY - lastMousePosRef.current.y;
        const direction = resizeDirectionRef.current;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let newWidth = sizeRef.current.width;
        let newHeight = sizeRef.current.height;
        let newX = positionRef.current.x;
        let newY = positionRef.current.y;

        // Handle resize based on direction (transform origin: top-left)
        if (direction.includes("right")) {
          newWidth = Math.max(
            350,
            Math.min(
              sizeRef.current.width + deltaX,
              viewportWidth - positionRef.current.x
            )
          );
        }
        if (direction.includes("left")) {
          newWidth = Math.max(
            350,
            Math.min(
              sizeRef.current.width - deltaX,
              positionRef.current.x + sizeRef.current.width
            )
          );
          newX = positionRef.current.x + (sizeRef.current.width - newWidth);
        }
        if (direction.includes("bottom")) {
          newHeight = Math.max(
            200,
            Math.min(
              sizeRef.current.height + deltaY,
              viewportHeight - positionRef.current.y
            )
          );
        }
        if (direction.includes("top")) {
          newHeight = Math.max(
            200,
            Math.min(
              sizeRef.current.height - deltaY,
              positionRef.current.y + sizeRef.current.height
            )
          );
          newY = positionRef.current.y + (sizeRef.current.height - newHeight);
        }

        setSize({ width: newWidth, height: newHeight });
        setPosition({ x: newX, y: newY });
        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      } else if (isDragging && modalRef.current) {
        const modalRect = modalRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate relative movement (delta)
        const deltaX = e.clientX - lastMousePosRef.current.x;
        const deltaY = e.clientY - lastMousePosRef.current.y;

        // Calculate new position based on delta from current position
        let newX = positionRef.current.x + deltaX;
        let newY = positionRef.current.y + deltaY;

        // Constrain to viewport boundaries
        newX = Math.max(0, Math.min(newX, viewportWidth - modalRect.width));
        newY = Math.max(0, Math.min(newY, viewportHeight - modalRect.height));

        setPosition({ x: newX, y: newY });
        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      resizeDirectionRef.current = "";
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, resizable]);

  const handleModalClick = () => {
    bringToTop(modalId);
  };

  const modalStyle: React.CSSProperties = {
    top: position.y,
    left: position.x,
    zIndex,
    transformOrigin: "top left",
    minWidth: minWidth,
    ...style,
  };

  if (resizable && size.width > 0 && size.height > 0) {
    modalStyle.width = size.width;
    modalStyle.height = size.height;
  } else if (!resizable) {
    // For non-resizable modals, ensure they size to content but can expand
    modalStyle.width = "max-content";
    modalStyle.height = "max-content";
  }

  return (
    <>
      <div className={styles.overlay} style={{ zIndex: zIndex - 1 }} />
      <div
        ref={modalRef}
        className={styles.modal + ` ${!isOpen ? styles.hide : ""}`}
        style={modalStyle}
        onClick={handleModalClick}
        onMouseDown={handleModalClick}
      >
        {/* Invisible resize handles on edges (only if resizable) */}
        {resizable && (
          <>
            {/* Top edge */}
            <div
              className="absolute top-0 left-0 right-0 h-1 cursor-ns-resize z-10"
              onMouseDown={handleResizeStart("top")}
            />
            {/* Bottom edge */}
            <div
              className="absolute bottom-0 left-0 right-0 h-1 cursor-ns-resize z-10"
              onMouseDown={handleResizeStart("bottom")}
            />
            {/* Left edge */}
            <div
              className="absolute top-0 bottom-0 left-0 w-1 cursor-ew-resize z-10"
              onMouseDown={handleResizeStart("left")}
            />
            {/* Right edge */}
            <div
              className="absolute top-0 bottom-0 right-0 w-1 cursor-ew-resize z-10"
              onMouseDown={handleResizeStart("right")}
            />
            {/* Corners */}
            <div
              className="absolute top-0 left-0 w-2 h-2 cursor-nwse-resize z-10"
              onMouseDown={handleResizeStart("top-left")}
            />
            <div
              className="absolute top-0 right-0 w-2 h-2 cursor-nesw-resize z-10"
              onMouseDown={handleResizeStart("top-right")}
            />
            <div
              className="absolute bottom-0 left-0 w-2 h-2 cursor-nesw-resize z-10"
              onMouseDown={handleResizeStart("bottom-left")}
            />
            <div
              className="absolute bottom-0 right-0 w-2 h-2 cursor-nwse-resize z-10"
              onMouseDown={handleResizeStart("bottom-right")}
            />
          </>
        )}

        <div className={styles.modalHeader} onMouseDown={handleDragStart}>
          <h2>{header}</h2>
          {closeModal && (
            <button onClick={closeModal}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className={styles.modalContent}>
          {children ?? <p>No content</p>}
        </div>
      </div>
    </>
  );
};

export default Modal;
